import { useCallback, useEffect, useState } from 'react';
import { TRPCMethodEnum, TRPCResourceEnum } from '../../../api/trpcApi/types';
import { useTRPCRequest } from '../../../hooks';
import { trpc } from '../../../libs/trpc';
import { useParams } from 'react-router-dom';
import Configurator from '../../../../../configurator-ui/src/features/app/Configurator';
import { configuratorStore } from '../../../../../configurator-ui/src/store';
import { Provider } from 'react-redux';
import { CartErrors } from '../../../../../configurator-ui/src/utils/getCartErrors';

type LabelSlot = {
  label_id: string;
  label_variant_id: string;
  material_id: string;
  slot_id: string;
};

type TextField = {
  text_field_id: string;
  value: string;
};

type CartItem = {
  cart_item_id: string;
  palette_id: string;
  product_id: string;
  font_id: string;
  icon_id: string;
  style_id: string;
  label_slots: LabelSlot[];
  text_fields: TextField[];
};

type AvailableProduct = {
  product_id: string;
  system_name: string;
  tag: string;
};

interface ItemProps {
  updateErrors?: (errors: CartErrors | undefined) => void;
  updateModified?: (mod: boolean) => void;
}
export const Item = ({ updateErrors, updateModified }: ItemProps) => {
  const { cart_item_id } = useParams();
  const { handleTRPCRequest } = useTRPCRequest();
  const [tag, setTag] = useState<string>();
  const [cartItem, setCartItem] = useState<CartItem>();
  const [fetchError, setFetchError] = useState<string>();
  const [needsReassignment, setNeedsReassignment] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<
    AvailableProduct[]
  >([]);

  const fetchOrder = useCallback(async () => {
    const { res: cartItem } = await handleTRPCRequest({
      method: TRPCMethodEnum.get,
      resourceType: TRPCResourceEnum.cartItems,
      requestBody: {
        cart_item_id: cart_item_id
      }
    });

    if (!cartItem) {
      setFetchError('Cart item not found.');
      updateModified?.(false);
      return;
    }

    const { res: product } = await handleTRPCRequest({
      method: TRPCMethodEnum.get,
      resourceType: TRPCResourceEnum.products,
      options: { disableNavigateOnError: true },
      requestBody: {
        product_id: cartItem.product_id
      }
    });

    if (!product) {
      // Fallback: look up by tag, which is stable across environments even when product_id differs
      const derivedTag = `product-design:${cartItem.product_id}`;
      const productByTag = await trpc.products.getByTag
        .query({ tag: derivedTag })
        .catch(() => null);

      if (!productByTag) {
        // Product can't be resolved automatically — prompt admin to reassign
        const products = await trpc.products.list.query({}).catch(() => []);
        setAvailableProducts(products as AvailableProduct[]);
        setCartItem(cartItem);
        setNeedsReassignment(true);
        updateModified?.(false);
        return;
      }

      setTag(productByTag.tag);
      setCartItem({ ...cartItem, product_id: productByTag.product_id });
      return;
    }

    setTag(product.tag);
    setCartItem(cartItem);
  }, [cart_item_id, handleTRPCRequest, updateModified]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const handleProductReassign = useCallback(
    (selected: AvailableProduct) => {
      setCartItem(prev =>
        prev ? { ...prev, product_id: selected.product_id } : prev
      );
      setTag(selected.tag);
      setNeedsReassignment(false);
      updateModified?.(true);
    },
    [updateModified]
  );

  if (fetchError) {
    return <p className="py-4 text-sm text-red-600">{fetchError}</p>;
  }

  if (needsReassignment) {
    return (
      <div className="py-4">
        <p className="mb-3 text-sm text-amber-600">
          The product linked to this cart item (ID:{' '}
          <code>{cartItem?.product_id}</code>) could not be found in this
          environment. Please select the correct product to continue editing.
        </p>
        <select
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          defaultValue=""
          onChange={e => {
            const selected = availableProducts.find(
              p => p.product_id === e.target.value
            );
            if (selected) handleProductReassign(selected);
          }}
        >
          <option value="" disabled>
            Select a product…
          </option>
          {availableProducts.map(p => (
            <option key={p.product_id} value={p.product_id}>
              {p.system_name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (!tag || !cartItem) {
    return <></>;
  }
  return (
    <Provider store={configuratorStore}>
      <Configurator
        admin={true}
        product_tag={tag}
        show={true}
        addToCartButton={false}
        state={{
          style_id: cartItem.style_id,
          palette_id: cartItem.palette_id,
          font_id: cartItem.font_id,
          icon_id: cartItem.icon_id,
          text_fields: cartItem.text_fields,
          label_slots: cartItem.label_slots
        }}
        updateErrors={updateErrors}
        updateModified={updateModified}
      />
    </Provider>
  );
};
